/**
 * GovCostAnalysisItemsTable — editable line item grid.
 * Grouped by category, inline-editable, with per-item source chips.
 */
import React, { useMemo, useState } from 'react';
import api from '../../services/api';
import { S, GOV_COLORS, fmtMoney, COST_TYPE_META, PRICING_METHOD_META, confidenceColor } from './govShared';

interface Props {
  versionId: string;
  items: any[];
  sources: any[];
  itemSources: any[];
  readOnly: boolean;
  onChanged: () => void;
}

const NEW_ITEM_TEMPLATE = {
  description: '',
  category: '',
  cost_type: 'other',
  quantity: 1,
  unit: 'LS',
  low_unit_cost: 0,
  expected_unit_cost: 0,
  high_unit_cost: 0,
  pricing_method: 'estimator_override',
  is_included: true,
  is_alternate: false,
};

export default function GovCostAnalysisItemsTable({ versionId, items, sources, itemSources, readOnly, onChanged }: Props) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<any>({});
  const [showNew, setShowNew] = useState(false);
  const [newDraft, setNewDraft] = useState<any>(NEW_ITEM_TEMPLATE);
  const [expandedSources, setExpandedSources] = useState<Set<string>>(new Set());

  const sourcesByItem = useMemo(() => {
    const m: Record<string, any[]> = {};
    for (const link of itemSources) {
      if (!m[link.item_id]) m[link.item_id] = [];
      const src = link.source || sources.find(s => s.id === link.source_id);
      if (src) m[link.item_id].push({ ...src, source_role: link.source_role, scenario: link.scenario });
    }
    return m;
  }, [itemSources, sources]);

  const grouped = useMemo(() => {
    const g: Record<string, any[]> = {};
    for (const it of items) {
      const key = it.category || 'Uncategorized';
      (g[key] ||= []).push(it);
    }
    return Object.entries(g).sort(([a], [b]) => a.localeCompare(b));
  }, [items]);

  const startEdit = (item: any) => {
    setEditingId(item.id);
    setDraft({ ...item });
  };
  const cancelEdit = () => { setEditingId(null); setDraft({}); };

  const saveEdit = async () => {
    if (!editingId) return;
    try {
      const patch: any = {};
      for (const k of ['description','category','csi_code','cost_code','cost_type','quantity','unit','low_unit_cost','expected_unit_cost','high_unit_cost','pricing_method','assumption','estimator_notes','is_included','is_alternate']) {
        if (k in draft) patch[k] = draft[k];
      }
      await api.patch(`/gov/cost-analysis-items/${editingId}`, patch);
      cancelEdit();
      onChanged();
    } catch (e: any) { alert(e?.response?.data?.error || 'Save failed'); }
  };

  const deleteItem = async (id: string) => {
    if (!window.confirm('Delete this line item?')) return;
    try { await api.delete(`/gov/cost-analysis-items/${id}`); onChanged(); }
    catch (e: any) { alert(e?.response?.data?.error || 'Delete failed'); }
  };

  const toggleInclude = async (item: any) => {
    try { await api.patch(`/gov/cost-analysis-items/${item.id}`, { is_included: !item.is_included }); onChanged(); }
    catch (e: any) { alert(e?.response?.data?.error || 'Toggle failed'); }
  };

  const createItem = async () => {
    if (!newDraft.description?.trim()) { alert('Description required'); return; }
    try {
      await api.post(`/gov/cost-analysis-versions/${versionId}/items`, newDraft);
      setShowNew(false);
      setNewDraft(NEW_ITEM_TEMPLATE);
      onChanged();
    } catch (e: any) { alert(e?.response?.data?.error || 'Create failed'); }
  };

  const toggleSourcesExpanded = (itemId: string) => {
    setExpandedSources(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId); else next.add(itemId);
      return next;
    });
  };

  return (
    <div style={{ ...S.card, padding: 0, overflow: 'hidden' }}>
      <div style={{ padding: '12px 16px', borderBottom: `1px solid ${GOV_COLORS.border}`, display: 'flex', alignItems: 'center', gap: 12 }}>
        <h3 style={{ ...S.panelTitle, marginBottom: 0 }}>Line Items ({items.length})</h3>
        <div style={{ flex: 1 }} />
        {!readOnly && (
          <button onClick={() => setShowNew(true)} style={{ ...S.addBtn, padding: '6px 14px', fontSize: 12 }}>+ Add Line Item</button>
        )}
      </div>

      {items.length === 0 ? (
        <div style={{ ...S.empty, padding: 40 }}>No line items yet. Generate an AI analysis or add items manually.</div>
      ) : (
        <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {grouped.map(([category, catItems]) => {
            const catTotal = catItems.reduce((s, i) => s + (i.is_included !== false ? Number(i.expected_extended_cost || 0) : 0), 0);
            return (
              <div key={category}>
                <div style={{ padding: '8px 16px', background: '#0F1923', display: 'flex', alignItems: 'center', gap: 8, borderTop: `1px solid ${GOV_COLORS.border}` }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: GOV_COLORS.textMuted, letterSpacing: 0.5, textTransform: 'uppercase' }}>{category}</span>
                  <span style={{ color: GOV_COLORS.textDim, fontSize: 11 }}>({catItems.length})</span>
                  <div style={{ flex: 1 }} />
                  <span style={{ color: GOV_COLORS.text, fontSize: 12, fontWeight: 700 }}>{fmtMoney(catTotal)}</span>
                </div>
                {catItems.map(item => {
                  const meta = COST_TYPE_META[item.cost_type] || COST_TYPE_META.other;
                  const isEditing = editingId === item.id;
                  const pmMeta = PRICING_METHOD_META[item.pricing_method];
                  const itemConfidence = item.quantity_confidence != null && item.price_confidence != null
                    ? (Number(item.quantity_confidence) + Number(item.price_confidence)) / 2
                    : null;
                  const itemSourcesList = sourcesByItem[item.id] || [];
                  const isExpanded = expandedSources.has(item.id);

                  return (
                    <div key={item.id} style={{
                      borderBottom: `1px solid ${GOV_COLORS.border}`,
                      opacity: item.is_included ? 1 : 0.5,
                      background: item.is_alternate ? '#1a2233' : 'transparent',
                    }}>
                      {isEditing ? (
                        <div style={{ padding: 12 }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 8, marginBottom: 8 }}>
                            <input type="text" value={draft.description || ''} onChange={e => setDraft({ ...draft, description: e.target.value })}
                              style={{ ...S.input, minWidth: 0, width: '100%' }} placeholder="Description" />
                            <select value={draft.cost_type} onChange={e => setDraft({ ...draft, cost_type: e.target.value })} style={S.select}>
                              {Object.entries(COST_TYPE_META).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
                            </select>
                            <select value={draft.pricing_method || 'estimator_override'} onChange={e => setDraft({ ...draft, pricing_method: e.target.value })} style={S.select}>
                              {Object.entries(PRICING_METHOD_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                            </select>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 8, marginBottom: 8 }}>
                            <input type="text" value={draft.category || ''} onChange={e => setDraft({ ...draft, category: e.target.value })} style={S.input} placeholder="Category" />
                            <input type="text" value={draft.csi_code || ''} onChange={e => setDraft({ ...draft, csi_code: e.target.value })} style={S.input} placeholder="CSI" />
                            <input type="number" value={draft.quantity} onChange={e => setDraft({ ...draft, quantity: e.target.value })} style={S.input} placeholder="Qty" />
                            <input type="text" value={draft.unit} onChange={e => setDraft({ ...draft, unit: e.target.value })} style={S.input} placeholder="Unit" />
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 8 }}>
                            <div>
                              <div style={{ ...S.kvLabel, marginBottom: 4 }}>Low unit $</div>
                              <input type="number" value={draft.low_unit_cost} onChange={e => setDraft({ ...draft, low_unit_cost: e.target.value })} style={{ ...S.input, width: '100%' }} />
                            </div>
                            <div>
                              <div style={{ ...S.kvLabel, marginBottom: 4 }}>Expected unit $</div>
                              <input type="number" value={draft.expected_unit_cost} onChange={e => setDraft({ ...draft, expected_unit_cost: e.target.value })} style={{ ...S.input, width: '100%' }} />
                            </div>
                            <div>
                              <div style={{ ...S.kvLabel, marginBottom: 4 }}>High unit $</div>
                              <input type="number" value={draft.high_unit_cost} onChange={e => setDraft({ ...draft, high_unit_cost: e.target.value })} style={{ ...S.input, width: '100%' }} />
                            </div>
                          </div>
                          <textarea value={draft.assumption || ''} onChange={e => setDraft({ ...draft, assumption: e.target.value })}
                            style={{ ...S.input, width: '100%', minHeight: 50, marginBottom: 8, resize: 'vertical' as const }}
                            placeholder="Assumption for this line (optional)" />
                          <div style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: GOV_COLORS.textMuted, fontSize: 12, cursor: 'pointer' }}>
                              <input type="checkbox" checked={draft.is_included !== false} onChange={e => setDraft({ ...draft, is_included: e.target.checked })} />
                              Included in totals
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: GOV_COLORS.textMuted, fontSize: 12, cursor: 'pointer' }}>
                              <input type="checkbox" checked={!!draft.is_alternate} onChange={e => setDraft({ ...draft, is_alternate: e.target.checked })} />
                              Alternate (excluded from base total)
                            </label>
                          </div>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button onClick={cancelEdit} style={{ ...S.cancelBtn, flex: 1 }}>Cancel</button>
                            <button onClick={saveEdit} style={{ ...S.addBtn, flex: 1 }}>Save</button>
                            <button onClick={() => deleteItem(item.id)} style={S.dangerBtn}>Delete</button>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div style={{
                            padding: '10px 16px',
                            display: 'grid',
                            gridTemplateColumns: '32px 1fr 90px 100px 100px 100px 32px',
                            gap: 12,
                            alignItems: 'center',
                            cursor: readOnly ? 'default' : 'pointer',
                          }} onClick={() => !readOnly && startEdit(item)}>
                            <div style={{ fontSize: 18 }}>{meta.icon}</div>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>
                                {item.description}
                                {item.is_alternate && <span style={{ ...S.chip, background: '#8B5CF622', color: '#8B5CF6', marginLeft: 8, fontSize: 10 }}>ALT</span>}
                                {item.is_manual_override && <span style={{ ...S.chip, background: '#F9731622', color: '#F97316', marginLeft: 8, fontSize: 10 }}>MANUAL</span>}
                                {!item.is_ai_generated && !item.is_manual_override && <span style={{ ...S.chip, background: '#3B82F622', color: '#3B82F6', marginLeft: 8, fontSize: 10 }}>USER</span>}
                              </div>
                              <div style={{ color: GOV_COLORS.textMuted, fontSize: 11, marginTop: 2, display: 'flex', gap: 8, alignItems: 'center' }}>
                                <span>{item.quantity} {item.unit}</span>
                                {item.csi_code && <span>· CSI {item.csi_code}</span>}
                                {pmMeta && <span>· {pmMeta.label}</span>}
                                {itemConfidence != null && <span style={{ color: confidenceColor(itemConfidence) }}>· {Math.round(itemConfidence)}%</span>}
                                {itemSourcesList.length > 0 && (
                                  <span onClick={e => { e.stopPropagation(); toggleSourcesExpanded(item.id); }} style={{ color: GOV_COLORS.accent, cursor: 'pointer' }}>
                                    · 📎 {itemSourcesList.length} source{itemSourcesList.length > 1 ? 's' : ''} {isExpanded ? '▲' : '▼'}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div style={{ textAlign: 'right', color: GOV_COLORS.success, fontSize: 12, fontWeight: 700 }}>{fmtMoney(item.low_extended_cost)}</div>
                            <div style={{ textAlign: 'right', color: '#fff', fontSize: 13, fontWeight: 800 }}>{fmtMoney(item.expected_extended_cost)}</div>
                            <div style={{ textAlign: 'right', color: GOV_COLORS.warning, fontSize: 12, fontWeight: 700 }}>{fmtMoney(item.high_extended_cost)}</div>
                            <div style={{ textAlign: 'right' }}>
                              {!readOnly && (
                                <input type="checkbox" checked={item.is_included !== false}
                                  onClick={e => e.stopPropagation()}
                                  onChange={() => toggleInclude(item)}
                                  title="Include in totals" />
                              )}
                            </div>
                            <div style={{ color: GOV_COLORS.textDim, fontSize: 16, textAlign: 'right' }}>{readOnly ? '' : '✎'}</div>
                          </div>
                          {isExpanded && itemSourcesList.length > 0 && (
                            <div style={{ padding: '0 16px 12px 60px', background: '#0F1923' }}>
                              {itemSourcesList.map((s: any, idx: number) => (
                                <div key={idx} style={{ padding: '6px 0', borderTop: idx > 0 ? `1px solid ${GOV_COLORS.border}` : 'none', fontSize: 12 }}>
                                  <div style={{ color: GOV_COLORS.text, fontWeight: 600 }}>
                                    {s.title}
                                    {s.locator && <span style={{ color: GOV_COLORS.textMuted, fontWeight: 400, marginLeft: 6 }}>· {s.locator}</span>}
                                    <span style={{ ...S.chip, background: GOV_COLORS.accent + '22', color: GOV_COLORS.accent, marginLeft: 8, fontSize: 10 }}>{s.source_role}</span>
                                  </div>
                                  {s.evidence_text && <div style={{ color: GOV_COLORS.textMuted, marginTop: 2, fontStyle: 'italic' }}>"{s.evidence_text}"</div>}
                                  {s.url && <a href={s.url} target="_blank" rel="noreferrer" style={{ color: GOV_COLORS.accent, fontSize: 11 }}>{s.url}</a>}
                                </div>
                              ))}
                            </div>
                          )}
                          {item.assumption && (
                            <div style={{ padding: '0 16px 8px 60px', color: GOV_COLORS.textMuted, fontSize: 11, fontStyle: 'italic' }}>
                              💡 {item.assumption}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      {showNew && (
        <div style={S.modalBackdrop} onClick={() => setShowNew(false)}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>
            <h2 style={S.modalTitle}>+ Add Line Item</h2>
            <div style={S.modalRow}>
              <label style={S.modalLabel}>Description *</label>
              <input type="text" value={newDraft.description} onChange={e => setNewDraft({ ...newDraft, description: e.target.value })} style={{ ...S.input, width: '100%' }} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
              <div>
                <label style={S.modalLabel}>Category</label>
                <input type="text" value={newDraft.category} onChange={e => setNewDraft({ ...newDraft, category: e.target.value })} style={{ ...S.input, width: '100%' }} placeholder="Sitework, Concrete, Mob, ..." />
              </div>
              <div>
                <label style={S.modalLabel}>Cost Type</label>
                <select value={newDraft.cost_type} onChange={e => setNewDraft({ ...newDraft, cost_type: e.target.value })} style={{ ...S.select, width: '100%' }}>
                  {Object.entries(COST_TYPE_META).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', gap: 12, marginBottom: 14 }}>
              <div>
                <label style={S.modalLabel}>Quantity</label>
                <input type="number" value={newDraft.quantity} onChange={e => setNewDraft({ ...newDraft, quantity: Number(e.target.value) })} style={{ ...S.input, width: '100%' }} />
              </div>
              <div>
                <label style={S.modalLabel}>Unit</label>
                <input type="text" value={newDraft.unit} onChange={e => setNewDraft({ ...newDraft, unit: e.target.value })} style={{ ...S.input, width: '100%' }} placeholder="LS, EA, SF, LF, ..." />
              </div>
              <div>
                <label style={S.modalLabel}>Pricing Method</label>
                <select value={newDraft.pricing_method} onChange={e => setNewDraft({ ...newDraft, pricing_method: e.target.value })} style={{ ...S.select, width: '100%' }}>
                  {Object.entries(PRICING_METHOD_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 14 }}>
              <div>
                <label style={S.modalLabel}>Low Unit $</label>
                <input type="number" value={newDraft.low_unit_cost} onChange={e => setNewDraft({ ...newDraft, low_unit_cost: Number(e.target.value) })} style={{ ...S.input, width: '100%' }} />
              </div>
              <div>
                <label style={S.modalLabel}>Expected Unit $</label>
                <input type="number" value={newDraft.expected_unit_cost} onChange={e => setNewDraft({ ...newDraft, expected_unit_cost: Number(e.target.value) })} style={{ ...S.input, width: '100%' }} />
              </div>
              <div>
                <label style={S.modalLabel}>High Unit $</label>
                <input type="number" value={newDraft.high_unit_cost} onChange={e => setNewDraft({ ...newDraft, high_unit_cost: Number(e.target.value) })} style={{ ...S.input, width: '100%' }} />
              </div>
            </div>
            <div style={S.modalFooter}>
              <button onClick={() => setShowNew(false)} style={{ ...S.cancelBtn, flex: 1 }}>Cancel</button>
              <button onClick={createItem} style={{ ...S.addBtn, flex: 2 }}>Add Item</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
